// =============================================================================
// SIREPPAR - Wizard de Rendición de Campaña Electoral (Art. 53 inc. c, Ley XII-23)
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { partidoService, rendicionCampanaService, TipoPresentacionRendicion } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ROLES_INTERNOS = ['ADMINISTRADOR', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'MESA_ENTRADAS', 'ANALISTA', 'AUDITOR'];
import type {
  RendicionCampana,
  RendicionCampanaListItem,
} from './rendicion-campana/types';
import { LABEL_ETAPA, LABEL_TIPO_ELECCION } from './rendicion-campana/types';
import Paso1Datos from './rendicion-campana/Paso1Datos';
import Paso2Aportes from './rendicion-campana/Paso2Aportes';
import Paso34Movimientos from './rendicion-campana/Paso34Movimientos';
import Paso5VistaPrevia from './rendicion-campana/Paso5VistaPrevia';
import Paso6FirmaEnvio from './rendicion-campana/Paso6FirmaEnvio';
import Paso6FirmaEnvioDistrital from './rendicion-campana/Paso6FirmaEnvioDistrital';
import VersionesDropdown from '../../components/common/VersionesDropdown';

// =============================================================================
// Stepper
// =============================================================================

const STEPS = [
  { num: 1, label: 'Datos del Acto Electoral' },
  { num: 2, label: 'Aportantes' },
  { num: 3, label: 'Ingresos de Campaña' },
  { num: 4, label: 'Egresos de Campaña' },
  { num: 5, label: 'Vista Previa del Informe' },
  { num: 6, label: 'Firma y Envío' },
];

interface StepperProps {
  current: number;
  onChange: (n: number) => void;
  rendicion: RendicionCampana | null;
  tipoPresentacion: TipoPresentacionRendicion;
  currentDirty?: boolean;
  erroresPorPaso?: Set<number>;
}

function Stepper({
  current,
  onChange,
  rendicion,
  tipoPresentacion,
  currentDirty,
  erroresPorPaso,
}: StepperProps) {
  const esCopia = tipoPresentacion === 'COPIA_DISTRITO';

  function handleClick(num: number) {
    if (num === current) return;
    if (currentDirty) {
      const ok = window.confirm(
        `Tenés cambios sin guardar en el paso ${current}. Si continuás, se van a perder.\n\n¿Continuar sin guardar?`,
      );
      if (!ok) return;
    }
    onChange(num);
  }

  return (
    <div className="flex gap-1 overflow-x-auto pb-2 mb-5 -mx-1 px-1">
      {STEPS.map((s) => {
        const deshabilitado = !rendicion && s.num > 1;
        const salteado = esCopia && s.num >= 2 && s.num <= 5;
        const activo = s.num === current;
        const completado = rendicion && s.num < current && !salteado;
        const tieneError = erroresPorPaso?.has(s.num);

        const base =
          'flex items-center gap-2 px-3 py-2 rounded-md text-xs whitespace-nowrap border-2 transition-all flex-shrink-0';
        const cls = activo
          ? tieneError
            ? `${base} bg-red-600 text-white border-red-700 font-semibold`
            : `${base} bg-blue-600 text-white border-blue-600 font-semibold`
          : completado && tieneError
          ? `${base} bg-red-50 text-red-800 border-red-300`
          : completado
          ? `${base} bg-green-50 text-green-800 border-green-300`
          : tieneError
          ? `${base} bg-red-50 text-red-800 border-red-300 hover:bg-red-100 cursor-pointer`
          : salteado
          ? `${base} bg-gray-100 text-gray-400 border-transparent italic`
          : deshabilitado
          ? `${base} bg-gray-100 text-gray-400 border-transparent cursor-not-allowed`
          : `${base} bg-gray-100 text-gray-600 border-transparent hover:bg-gray-200 cursor-pointer`;

        return (
          <button
            key={s.num}
            type="button"
            disabled={deshabilitado || salteado}
            onClick={() => !deshabilitado && !salteado && handleClick(s.num)}
            className={cls}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                activo
                  ? 'bg-white/30'
                  : completado && tieneError
                  ? 'bg-red-600 text-white'
                  : completado
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-400 text-white'
              }`}
            >
              {s.num}
            </span>
            <span className="hidden sm:inline">{s.label}</span>
            {activo && currentDirty && (
              <span
                title="Cambios sin guardar"
                className="ml-1 w-2 h-2 rounded-full bg-amber-400"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Selector de rendición
// =============================================================================

interface SelectorProps {
  partidoId: string;
  onSelect: (id: string) => void;
}

function RendicionesSelector({ partidoId, onSelect }: SelectorProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['rendiciones-campana-partido', partidoId],
    queryFn: async () => {
      const res = await rendicionCampanaService.listByPartido(partidoId);
      return (res.data ?? []) as RendicionCampanaListItem[];
    },
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Rendiciones de campaña</h2>
        <button
          type="button"
          onClick={() => onSelect('NUEVA')}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          + Nueva rendición
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 text-center py-6">Cargando...</p>
      ) : !data || data.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">
          Este partido aún no presentó ninguna rendición de campaña. Use{' '}
          <strong>Nueva rendición</strong> para iniciar.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Acto electoral</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Etapa</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-semibold">{LABEL_TIPO_ELECCION[r.tipoEleccion]}</td>
                  <td className="px-3 py-2 text-gray-700">{LABEL_ETAPA[r.etapa]}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {new Date(r.fechaActoElectoral).toLocaleDateString('es-AR')}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        r.tipoPresentacion === 'COMPLETA'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {r.tipoPresentacion === 'COMPLETA' ? 'Completa' : 'Copia distrito'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {r.expedienteId ? (
                      <Link
                        to={
                          r.estado === 'OBSERVADA'
                            ? `/expedientes/${r.expedienteId}?tab=obs`
                            : `/expedientes/${r.expedienteId}`
                        }
                        className="inline-flex items-center gap-1.5 hover:opacity-80"
                        title={r.estado === 'OBSERVADA' ? 'Ver observación' : 'Ver expediente'}
                      >
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            r.estado === 'BORRADOR'
                              ? 'bg-gray-100 text-gray-700'
                              : r.estado === 'PRESENTADA' || r.estado === 'EN_REVISION'
                              ? 'bg-blue-100 text-blue-800'
                              : r.estado === 'APROBADA'
                              ? 'bg-green-100 text-green-800'
                              : r.estado === 'DESAPROBADA'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {r.estado}
                        </span>
                        <span className="text-blue-500 text-sm font-bold underline underline-offset-1" aria-hidden="true">↗</span>
                      </Link>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          r.estado === 'BORRADOR'
                            ? 'bg-gray-100 text-gray-700'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {r.estado}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onSelect(r.id)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Abrir →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Página principal
// =============================================================================

export default function RendicionCampanaPage() {
  const { partidoId } = useParams<{ partidoId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const esInterno = ROLES_INTERNOS.includes(user?.rol || '');

  const rendicionParam = searchParams.get('rendicion');
  const versionViewParam = searchParams.get('versionView');
  const versionView = versionViewParam ? parseInt(versionViewParam, 10) : null;
  const enModoVistaVersion = versionView !== null && !Number.isNaN(versionView);
  const [modo, setModo] = useState<'LIST' | 'NUEVA' | 'EDIT'>('LIST');
  const [rendicionId, setRendicionId] = useState<string | null>(null);
  const [step, setStep] = useState(1);
  const [tipoPres, setTipoPres] = useState<TipoPresentacionRendicion>('COMPLETA');
  const [dirtyByStep, setDirtyByStep] = useState<Record<number, boolean>>({});

  const reportDirty = useCallback((stepNum: number, dirty: boolean) => {
    setDirtyByStep((prev) => {
      if (prev[stepNum] === dirty) return prev;
      return { ...prev, [stepNum]: dirty };
    });
  }, []);

  useEffect(() => {
    if (rendicionParam && rendicionParam !== 'nueva') {
      setRendicionId(rendicionParam);
      setModo('EDIT');
    } else if (rendicionParam === 'nueva') {
      setRendicionId(null);
      setModo('NUEVA');
    } else {
      setModo('LIST');
      setRendicionId(null);
    }
  }, [rendicionParam]);

  const { data: partido } = useQuery({
    queryKey: ['partido', partidoId],
    queryFn: async () => {
      if (!partidoId) return null;
      const res = await partidoService.getById(partidoId);
      return res.data.data;
    },
    enabled: !!partidoId,
  });

  const { data: rendicion, refetch } = useQuery({
    queryKey: ['rendicion-campana', rendicionId],
    queryFn: async () => {
      if (!rendicionId) return null;
      const res = await rendicionCampanaService.getById(rendicionId);
      return res.data as RendicionCampana;
    },
    enabled: !!rendicionId,
  });

  // Snapshot de una versión histórica
  const { data: snapshotResp } = useQuery({
    queryKey: ['rendicion-campana-snapshot', rendicionId, versionView],
    queryFn: async () => {
      if (!rendicionId || versionView === null) return null;
      const res = await rendicionCampanaService.getVersionSnapshot(rendicionId, versionView);
      return res.data as {
        id: string;
        numeroVersion: number;
        hashCode: string;
        fechaGeneracion: string;
        snapshotData: any;
      };
    },
    enabled: !!rendicionId && enModoVistaVersion,
    staleTime: Infinity,
  });

  // Inyectar datos del snapshot en cache cuando entra en modo lectura
  useEffect(() => {
    if (!enModoVistaVersion || !snapshotResp?.snapshotData || !rendicionId) return;
    const snap = snapshotResp.snapshotData;

    // Candidaturas → array directo
    if (Array.isArray(snap.candidaturas)) {
      const items = snap.candidaturas.map((c: any) => ({
        ...c,
        rendicionCampanaId: rendicionId,
        creadoEn: snapshotResp.fechaGeneracion,
      }));
      queryClient.setQueryData(['candidaturas-campana', rendicionId], items);
    }

    // Aportes → shape { items, totales }
    if (Array.isArray(snap.aportes)) {
      const items = snap.aportes.map((a: any) => ({
        ...a,
        rendicionCampanaId: rendicionId,
        alertaProveedorEstado: false,
        creadoEn: snapshotResp.fechaGeneracion,
      }));
      let humanasDinero = 0;
      let humanasEspecie = 0;
      let juridicasDinero = 0;
      let juridicasEspecie = 0;
      let total = 0;
      for (const a of items) {
        const importe = Number(a.importe);
        total += importe;
        if (a.tipoPersona === 'HUMANA') {
          if (a.modalidad === 'ESPECIE') humanasEspecie += importe;
          else humanasDinero += importe;
        } else {
          if (a.modalidad === 'ESPECIE') juridicasEspecie += importe;
          else juridicasDinero += importe;
        }
      }
      queryClient.setQueryData(['rendicion-campana-aportes', rendicionId], {
        items,
        totales: { humanasDinero, humanasEspecie, juridicasDinero, juridicasEspecie, total, cantidadAlertas: 0 },
      });
    }

    // Movimientos → shape { movimientos, resumen }
    if (Array.isArray(snap.movimientos)) {
      const movimientos = snap.movimientos.map((m: any) => ({
        ...m,
        rendicionCampanaId: rendicionId,
      }));
      let totalIngresos = 0;
      let totalEgresos = 0;
      for (const m of movimientos) {
        const importe = Number(m.importe);
        if (m.tipo === 'INGRESO') totalIngresos += importe;
        else totalEgresos += importe;
      }
      queryClient.setQueryData(['rendicion-campana-movimientos', rendicionId], {
        movimientos,
        resumen: {
          totalIngresos,
          totalEgresos,
          resultado: totalIngresos - totalEgresos,
        },
      });
    }
  }, [enModoVistaVersion, snapshotResp, rendicionId, queryClient]);

  function verVersion(numeroVersion: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('versionView', String(numeroVersion));
      return next;
    });
    setStep(1);
  }

  function salirVistaVersion() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('versionView');
      return next;
    });
    if (rendicionId) {
      queryClient.invalidateQueries({ queryKey: ['candidaturas-campana', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-aportes', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-movimientos', rendicionId] });
    }
  }

  const { data: validacionesShell } = useQuery({
    queryKey: ['rendicion-campana-validaciones', rendicionId],
    queryFn: async () => {
      if (!rendicionId) return null;
      const res = await rendicionCampanaService.getValidaciones(rendicionId);
      return res.data as { ok: boolean; validaciones: { codigo: string; ok: boolean }[] };
    },
    enabled: !!rendicionId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const erroresPorPaso = useMemo(() => {
    const set = new Set<number>();
    if (!validacionesShell) return set;
    const codigoToPaso: Record<string, number> = {
      candidaturas_declaradas: 1,
      aportantes_identificados: 2,
      aportes_coinciden_con_rubros: 3,
      movimientos_cargados: 3,
    };
    for (const v of validacionesShell.validaciones) {
      if (v.ok) continue;
      const paso = codigoToPaso[v.codigo];
      if (paso) set.add(paso);
    }
    return set;
  }, [validacionesShell]);

  useEffect(() => {
    if (rendicion) setTipoPres(rendicion.tipoPresentacion);
  }, [rendicion]);

  function openRendicion(id: string) {
    if (id === 'NUEVA') {
      setSearchParams({ rendicion: 'nueva' });
      setStep(1);
    } else {
      setSearchParams({ rendicion: id });
      setStep(1);
    }
  }

  function volverListado() {
    setSearchParams({});
    setStep(1);
  }

  if (!partidoId) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">Falta el ID del partido.</p>
        <Link to="/financiamiento" className="text-sm text-blue-600 hover:underline">
          ← Volver a Financiamiento
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        {modo === 'LIST' && 
        <Link to="/financiamiento" className="text-sm text-blue-600 hover:underline inline-block mb-2">
          ← Volver a Financiamiento
        </Link>
        }
        {(modo === 'NUEVA' || modo === 'EDIT') && (
            <button
              type="button"
              onClick={volverListado}
              className="text-sm text-blue-600 hover:underline inline-block mb-2"
            >
              ← Volver a Rendiciones
            </button>
        )}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rendición de Campaña</h1>
            {partido && (
              <p className="mt-1 text-sm text-gray-500">
                {(partido as any).denominacion} ({(partido as any).sigla}) · Art. 53 inc. c, Ley XII-23
              </p>
            )}
          </div>
        </div>
      </div>

      {modo === 'LIST' && <RendicionesSelector partidoId={partidoId} onSelect={openRendicion} />}

      {(modo === 'NUEVA' || modo === 'EDIT') && (
        <>
          {modo === 'EDIT' && rendicion && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-700 flex items-center justify-between flex-wrap gap-2">
              <div>
                <strong>{LABEL_TIPO_ELECCION[rendicion.tipoEleccion]}</strong> ·{' '}
                {LABEL_ETAPA[rendicion.etapa]} ·{' '}
                {new Date(rendicion.fechaActoElectoral).toLocaleDateString('es-AR')}
                {rendicion.fechaVencimiento && (
                  <>
                    {' '}· Plazo:{' '}
                    <span className="text-amber-700 font-semibold">
                      {new Date(rendicion.fechaVencimiento).toLocaleDateString('es-AR')}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    rendicion.estado === 'BORRADOR'
                      ? 'bg-gray-200 text-gray-700'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {rendicion.estado}
                </span>
                <span className="text-xs text-gray-500">Versión {rendicion.versionActual}</span>
                <VersionesDropdown
                  rendicionId={rendicion.id}
                  versionActual={rendicion.versionActual}
                  service={rendicionCampanaService}
                  queryKey="rendicion-campana-versiones-dropdown"
                  onVerVersion={verVersion}
                  versionEnVista={enModoVistaVersion ? versionView : null}
                />
              </div>
            </div>
          )}

          {enModoVistaVersion && (
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-4 py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-amber-900">
                <strong>Estás viendo la versión N° {versionView}</strong>
                {snapshotResp?.fechaGeneracion && (
                  <span className="text-amber-800">
                    {' '}· Generada el {new Date(snapshotResp.fechaGeneracion).toLocaleString('es-AR')}
                  </span>
                )}
                <span className="block text-xs text-amber-700 mt-0.5">
                  Modo solo lectura — los datos mostrados son el snapshot guardado al firmar esa versión.
                </span>
              </div>
              <button
                type="button"
                onClick={salirVistaVersion}
                className="text-xs font-medium text-amber-900 underline hover:text-amber-700"
              >
                Volver a la versión actual
              </button>
            </div>
          )}

          {!enModoVistaVersion && rendicion?.estado === 'OBSERVADA' && !esInterno && (
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg px-4 py-3">
              <div className="text-sm text-amber-900">
                <strong>
                  Esta rendición fue observada:{' '}
                  {rendicion.expedienteId ? (
                    <Link
                      to={`/expedientes/${rendicion.expedienteId}?tab=obs`}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      VER OBSERVACIÓN
                    </Link>
                  ) : null}
                </strong>
                <span className="block text-xs text-amber-800 mt-0.5">
                  Corrija lo observado por el analista, generá una nueva versión, re-envia y responde
                  la observación. El expediente pasará nuevamente a análisis.
                </span>
              </div>
            </div>
          )}

          {!enModoVistaVersion && esInterno && (
            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg px-4 py-3">
              <div className="text-sm text-blue-900">
                <strong>Modo solo lectura</strong>
                <span className="block text-xs text-blue-800 mt-0.5">
                  Estás navegando esta rendición como usuario interno. Para acciones (observar,
                  elevar, aprobar) usá la pestaña "Resumen Financiero" del expediente.
                </span>
              </div>
            </div>
          )}

          <Stepper
            current={step}
            onChange={setStep}
            rendicion={rendicion ?? null}
            tipoPresentacion={tipoPres}
            currentDirty={!!dirtyByStep[step]}
            erroresPorPaso={erroresPorPaso}
          />

          {step === 1 && (
            <Paso1Datos
              partidoId={partidoId}
              partido={(partido as any) ?? null}
              rendicion={rendicion ?? null}
              snapshotMode={enModoVistaVersion}
              onCreated={(r) => {
                setRendicionId(r.id);
                setSearchParams({ rendicion: r.id });
                refetch();
              }}
              onNext={() => {
                if (tipoPres === 'COMPLETA') setStep(2);
                else setStep(6);
              }}
              onChangeTipo={setTipoPres}
            />
          )}

          {step === 2 && rendicionId && tipoPres === 'COMPLETA' && (
            <Paso2Aportes
              rendicionId={rendicionId}
              readonly={enModoVistaVersion || esInterno || (!!rendicion && rendicion.estado !== 'BORRADOR' && rendicion.estado !== 'OBSERVADA')}
              snapshotMode={enModoVistaVersion}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && rendicionId && tipoPres === 'COMPLETA' && (
            <Paso34Movimientos
              rendicionId={rendicionId}
              tipo="INGRESO"
              readonly={enModoVistaVersion || esInterno || (!!rendicion && rendicion.estado !== 'BORRADOR' && rendicion.estado !== 'OBSERVADA')}
              snapshotMode={enModoVistaVersion}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              onDirtyChange={(d) => reportDirty(3, d)}
            />
          )}

          {step === 4 && rendicionId && tipoPres === 'COMPLETA' && (
            <Paso34Movimientos
              rendicionId={rendicionId}
              tipo="EGRESO"
              readonly={enModoVistaVersion || esInterno || (!!rendicion && rendicion.estado !== 'BORRADOR' && rendicion.estado !== 'OBSERVADA')}
              snapshotMode={enModoVistaVersion}
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
              onDirtyChange={(d) => reportDirty(4, d)}
            />
          )}

          {step === 5 && rendicionId && tipoPres === 'COMPLETA' && (
            enModoVistaVersion ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-600">
                La vista previa no está disponible en modo versión histórica.
                Para verla, abrí el PDF desde el desplegable "Ver anteriores".
              </div>
            ) : (
              <Paso5VistaPrevia
                rendicionId={rendicionId}
                onBack={() => setStep(4)}
                onNext={() => setStep(6)}
              />
            )
          )}

          {step === 6 && rendicionId && rendicion && (
            enModoVistaVersion ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-600">
                La firma y envío no están disponibles en vista de versión histórica.
                Salí de la vista para volver a operar sobre la versión actual.
              </div>
            ) : tipoPres === 'COPIA_DISTRITO' ? (
              <Paso6FirmaEnvioDistrital
                rendicion={rendicion}
                partidoId={partidoId}
                onBack={() => setStep(1)}
                onPresentada={({ expedienteId, esRepresentacion }) => {
                  const url = esRepresentacion
                    ? `/expedientes/${expedienteId}?tab=obs&precargarRespuesta=1`
                    : `/expedientes/${expedienteId}`;
                  toast.success(esRepresentacion ? 'Redirigiendo para responder la observación...' : 'Redirigiendo al expediente...');
                  setTimeout(() => navigate(url), 800);
                }}
              />
            ) : (
              <Paso6FirmaEnvio
                rendicion={rendicion}
                partidoId={partidoId}
                onBack={() => setStep(5)}
                onPresentada={({ expedienteId, esRepresentacion }) => {
                  const url = esRepresentacion
                    ? `/expedientes/${expedienteId}?tab=obs&precargarRespuesta=1`
                    : `/expedientes/${expedienteId}`;
                  toast.success(esRepresentacion ? 'Redirigiendo para responder la observación...' : 'Redirigiendo al expediente...');
                  setTimeout(() => navigate(url), 800);
                }}
              />
            )
          )}
        </>
      )}
    </div>
  );
}
